import React, { useEffect, useState, useRef } from 'react';
import '../styles/EpisodeCard.css';
import '../styles/EpisodeLayout.css';
import { API_BASE_URL } from '../config';
import EpisodeCard from '../components/EpisodeCard';
import { Episode } from '../types/Episode';

const EpisodesPage: React.FC = () => {
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSport, setSelectedSport] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [showFilters, setShowFilters] = useState(true);

  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [showFilters]);

  useEffect(() => {
    const fetchEpisodes = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/episodes`);
        const data = await response.json();
        const withAudio = data.filter((ep: Episode) => ep.audioFile);
        setEpisodes(withAudio);
      } catch (error) {
        console.error('Error fetching episodes:', error);
      }
    };

    fetchEpisodes();
  }, []);

  const uniqueSports = Array.from(
    new Set(episodes.map(ep => ep.sportName).filter(Boolean))
  );

  const uniqueCategories = Array.from(
    new Set(episodes.map(ep => ep.episodeCategoryName).filter(Boolean))
  );

  const filteredEpisodes = episodes
    .filter(ep =>
      ep.episodeName.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (selectedSport === '' || ep.sportName === selectedSport) &&
      (selectedCategory === '' || ep.episodeCategoryName === selectedCategory)
    )
    .sort((a, b) => {
      const dateA = new Date(a.datePublished).getTime();
      const dateB = new Date(b.datePublished).getTime();
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

  return (
    <div className="episodes-page-wrapper">
      <div className="episodes-header" ref={headerRef}>
        <h1 className="episodes-title">Episodes</h1>
        <button
          className="filters-toggle"
          onClick={() => setShowFilters(prev => !prev)}
        >
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>
        {showFilters && (
          <div className="episode-filters">
            <div className="filter-group">
  <label htmlFor="searchTerm">Keyword</label>
  <div className="filter-wrapper">
    <input
      id="searchTerm"
      type="text"
      className="filter-input"
      placeholder="Search episodes..."
      value={searchTerm}
      onChange={e => setSearchTerm(e.target.value)}
    />
  </div>
</div>
<div className="filter-group">
  <label htmlFor="sportFilter">Sport</label>
  <div className="filter-wrapper">
    <select
      id="sportFilter"
      className="filter-dropdown"
      value={selectedSport}
      onChange={e => setSelectedSport(e.target.value)}
    >
      <option value="">All Sports</option>
      {uniqueSports.map(sport => (
        <option key={sport} value={sport}>{sport}</option>
      ))}
    </select>
  </div>
</div>

<div className="filter-group">
  <label htmlFor="categoryFilter">Category</label>
  <div className="filter-wrapper">
    <select
      id="categoryFilter"
      className="filter-dropdown"
      value={selectedCategory}
      onChange={e => setSelectedCategory(e.target.value)}
    >
      <option value="">All Categories</option>
      {uniqueCategories.map(cat => (
        <option key={cat} value={cat}>{cat}</option>
      ))}
    </select>
  </div>
</div>

<div className="filter-group">
  <label htmlFor="sortOrder">Sort By</label>
  <div className="filter-wrapper">
    <select
      id="sortOrder"
      className="filter-dropdown"
      value={sortOrder}
      onChange={e => setSortOrder(e.target.value as 'newest' | 'oldest')}
    >
      <option value="newest">Newest to Oldest</option>
      <option value="oldest">Oldest to Newest</option>
    </select>
  </div>
</div>
        </div>
        )}
      </div>

      <div className="episode-list" style={{ paddingTop: `${headerHeight}px` }}>
        {filteredEpisodes.map((ep, idx) => (
          <EpisodeCard key={idx} episode={ep} />
        ))}
      </div>
    </div>
  );
};

export default EpisodesPage;